/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { FC, ReactNode } from 'react';
import { Stack, Divider, Skeleton, useTheme } from '@mui/material';

export interface ActivityDetailsSkeletonProps {
  headerContent?: ReactNode;
  contactBackgroundColor?: string;
}

const ActivityDetailsSkeleton: FC<ActivityDetailsSkeletonProps> = ({
  headerContent,
  contactBackgroundColor,
}): JSX.Element => {
  const theme = useTheme();
  const backgroundColor = contactBackgroundColor ?? theme.colors.primary50;

  return (
    <>
      {headerContent}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        gap={2}
        mb={2}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Skeleton variant="text" width="60%" height={48} sx={{ fontSize: '2rem' }} />
      </Stack>
      <Stack
        flex={2}
        gap={3}
        sx={{
          minWidth: { xs: '100%', md: 639 },
          maxWidth: { xs: '100%', md: 738 },
          width: { xs: '100%', md: 'auto' },
        }}
      >
        <Skeleton variant="text" width="40%" height={28} sx={{ fontSize: '1.125rem' }} />

        <Stack gap={0.5}>
          <Skeleton variant="text" width={80} height={24} sx={{ fontSize: '1rem' }} />
          <Skeleton variant="rounded" width={260} height={48} />
        </Stack>

        <Divider />

        {[1, 2, 3, 4].map((index) => (
          <div key={index}>
            <Skeleton variant="text" width={120} height={24} sx={{ fontSize: '1rem', mb: 0.5 }} />
            <Stack direction="row" gap={1} alignItems="center" marginBottom={2}>
              <Skeleton variant="circular" width={24} height={24} />
              <Skeleton variant="text" width="70%" height={24} sx={{ fontSize: '1rem' }} />
            </Stack>
            <Divider />
          </div>
        ))}

        <Stack gap={2}>
          <Skeleton variant="text" width={180} height={32} sx={{ fontSize: '1.5rem' }} />
          <Stack
            direction='row'
            gap={2}
            alignItems='center'
            sx={{
              backgroundColor,
              borderRadius: 2,
              padding: 2,
              minHeight: '4rem',
            }}
          >
            <Stack gap={1} width="100%">
              <Skeleton variant="text" width="50%" height={24} sx={{ fontSize: '1rem' }} />
              <Stack direction="row" alignItems="center" gap={2} marginBottom={1}>
                <Skeleton variant="circular" width={20} height={20} />
                <Skeleton variant="text" width="60%" height={20} sx={{ fontSize: '1rem' }} />
              </Stack>
              <Stack direction="row" alignItems="center" gap={2}>
                <Skeleton variant="circular" width={20} height={20} />
                <Skeleton variant="text" width="50%" height={20} sx={{ fontSize: '1rem' }} />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </>
  );
};

export default ActivityDetailsSkeleton;

